import * as React from "react";
import {
  Card,
  CardHeader,
  CardFooter,
  Text,
  Link,
  Button,
  Toolbar,
  ToolbarButton,
  Badge,
  tokens,
} from "@fluentui/react-components";
import {
  ChevronDownRegular,
  ChevronUpRegular,
  Delete20Regular,
  Mail20Regular,
  MailArrowDown20Regular,
  MailArrowUp20Regular,
  ArrowReplyRegular,
  ArrowReplyAllRegular,
  ArrowForwardRegular,
} from "@fluentui/react-icons";
import UserAvatar from "../../users/components/UserAvatar";
import timeAgo from "../../../shared/utils/timeAgo";
import ItemDeleteDialog from "../../../shared/components/ItemDeleteDialog";
import Files from "../../files/components/Files";

const TicketMailCard = ({
  ticketUpdate,
  t,
  styles,
  showTranslateLink,
  handleTranslate,
  translateMutation,
  showTranslation,
  translatedContent,
  showEmailDetails,
  setShowEmailDetails,
  retryMutation,
  isDeleteDialogOpen,
  setIsDeleteDialogOpen,
  useDeleteTicketUpdate,
  onEmailReply,
}) => {
  const canBeRemoved = ticketUpdate.update_type === "outgoing_email" && ticketUpdate.status === "failed";

  return (
    <Card
      id="ticket-email-card"
      className={styles.card}
      style={{ display: "flex", flexDirection: "column", minHeight: "auto", overflow: "visible" }}
    >
      <CardHeader
        className={styles.header}
        header={
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                {ticketUpdate.update_type === "outgoing_email" ? <MailArrowUp20Regular /> : <MailArrowDown20Regular />}
                {ticketUpdate.update_type === "outgoing_email" ? (
                  <Text weight="semibold">
                    <span>{t("ticket.userSentEmail", { name: ticketUpdate.author })}</span>
                    <span>
                      {" "}
                      {t("generic.to", { defaultValue: "To" }).toLowerCase()} {ticketUpdate.to_address}
                    </span>
                  </Text>
                ) : (
                  <Text weight="semibold">
                    {t("ticket.emailReceivedFrom", { name: ticketUpdate.author ?? ticketUpdate.from_address })}
                  </Text>
                )}
              </div>
              {showTranslateLink && (
                <Link onClick={handleTranslate} style={{ fontSize: "12px" }}>
                  {translateMutation.isPending
                    ? t("nounPhrases.translating", { defaultValue: "Translating..." })
                    : showTranslation
                      ? t("nounPhrases.showOriginal", { defaultValue: "Show original" })
                      : t("nounPhrases.showTranslation", { defaultValue: "Translate" })}
                </Link>
              )}
            </div>
            <span style={{ fontSize: "12px", color: "#666", display: "flex", alignItems: "center" }}>
              {timeAgo(ticketUpdate.created_at)}
            </span>
          </div>
        }
        image={
          <UserAvatar
            user={{
              id: ticketUpdate.author_id,
              name: ticketUpdate.author,
              public_photo_id: ticketUpdate.public_author_photo_id,
            }}
          />
        }
        action={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Badge
              className={`${ticketUpdate.update_type === "outgoing_email" ? styles.badgeOutgoing : styles.badgeIncoming}`}
              shape="circular"
              appearance="filled"
              style={{
                backgroundColor: ticketUpdate.update_type === "outgoing_email" ? "#43A047" : "#1E88E5",
                color: "#FFFFFF",
                width: "fit-content",
                margin: "0",
                padding: "0 6px",
              }}
              aria-label={
                ticketUpdate.update_type === "outgoing_email"
                  ? t("ticket.outgoingEmail", { defaultValue: "Outgoing email" }).toLocaleLowerCase()
                  : t("ticket.incomingEmail", { defaultValue: "Incoming email" }).toLocaleLowerCase()
              }
            >
              {ticketUpdate.update_type === "outgoing_email"
                ? t("nounPhrases.outgoingEmail", { defaultValue: "Outgoing email" }).toLocaleLowerCase()
                : t("nounPhrases.incomingEmail", { defaultValue: "Incoming email" }).toLocaleLowerCase()}
            </Badge>
            <Toolbar size="small">
              <ToolbarButton
                appearance="subtle"
                onClick={() => onEmailReply?.(ticketUpdate, "reply")}
                icon={<ArrowReplyRegular style={{ color: "#0078D4" }} />}
                title={t("generic.reply", { defaultValue: "Reply" })}
              />
              <ToolbarButton
                appearance="subtle"
                onClick={() => onEmailReply?.(ticketUpdate, "replyAll")}
                icon={<ArrowReplyAllRegular style={{ color: "#107C10" }} />}
                title={t("generic.replyAll", { defaultValue: "Reply all" })}
              />
              <ToolbarButton
                appearance="subtle"
                onClick={() => onEmailReply?.(ticketUpdate, "forward")}
                icon={<ArrowForwardRegular style={{ color: "#D83B01" }} />}
                title={t("generic.forward", { defaultValue: "Forward" })}
              />
              <ToolbarButton
                appearance="subtle"
                disabled={!canBeRemoved}
                icon={<Delete20Regular />}
                onClick={() => setIsDeleteDialogOpen(true)}
              />
            </Toolbar>
          </div>
        }
      />

      <div className={styles.body} style={{ minHeight: "auto", overflow: "visible", flex: 1 }}>
        {ticketUpdate.status === "failed" && (
          <div
            style={{
              backgroundColor: tokens.colorPaletteRedBackground1,
              color: tokens.colorPaletteRedForeground1,
              border: `1px solid ${tokens.colorPaletteRedBorder1}`,
              borderRadius: tokens.borderRadiusSmall,
              padding: "6px 8px",
              marginBottom: "8px",
              fontSize: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
              <span>
                {ticketUpdate.error_message || t("ticket.emailFailed", { defaultValue: "Email failed to send." })}
              </span>
              <Button
                size="small"
                appearance="outline"
                icon={<Mail20Regular />}
                onClick={() => retryMutation.mutate(ticketUpdate.public_id)}
                disabled={retryMutation.isPending}
              >
                {t("ticket.retryEmail", { defaultValue: "Retry" })}
              </Button>
            </div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <b>{t("generic.subject")}: </b>
            {ticketUpdate.subject}
            <Button
              style={{ alignItems: "center", height: "16px", marginLeft: "8px" }}
              icon={showEmailDetails ? <ChevronUpRegular /> : <ChevronDownRegular />}
              size="small"
              onClick={() => setShowEmailDetails(!showEmailDetails)}
            />
          </span>
          {showEmailDetails && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span>
                <b>{t("generic.from")}: </b>
                {ticketUpdate.from_address}
              </span>
              <span>
                <b>{t("generic.to")}: </b>
                {ticketUpdate.to_address}
              </span>
              {ticketUpdate.cc_address && (
                <span>
                  <b>{t("generic.cc")}: </b>
                  {ticketUpdate.cc_address}
                </span>
              )}
              <div style={{ borderTop: "1px solid #e1e1e1", margin: "12px 0" }} />
            </div>
          )}
        </div>
        <div
          style={{
            fontFamily: "inherit",
            fontSize: "14px",
            lineHeight: "1.5",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            minHeight: "auto",
            overflow: "visible",
            width: "100%",
          }}
          dangerouslySetInnerHTML={{
            __html: showTranslation && translatedContent ? translatedContent : ticketUpdate.content,
          }}
        />
      </div>

      <CardFooter
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
          padding: "4px",
        }}
      >
        <Files entity_type="ticket_update" entity_id={ticketUpdate.id} />
      </CardFooter>

      <ItemDeleteDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        useMutationHook={useDeleteTicketUpdate}
        id={ticketUpdate.public_id}
        name={t("generic.note", { defaultValue: "note" }).toLocaleLowerCase()}
        queryKey={["TicketUpdates", ticketUpdate.ticket_id]}
      />
    </Card>
  );
};

export default TicketMailCard;
